/* eslint-disable consistent-return */
import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { LayoutPage, LayoutContent, FormRenderer, Form, Errors, WarningModal } from 'components';
import { useStoreValue, APOLLO_NETWORK_ERROR, APOLLO_UNKNOWN_ERROR } from 'store';
import { config } from '_config';
import { initFormConfig, submitHandler, isAU, shouldShowAch, getNegativeCapacity } from 'utils';
import { useEventTracking } from 'react-event-tracker';
import { getDataLayerElements } from 'utils/getDataLayerElements';
import { byCountry } from 'utils/byConfig';
import { useWarning } from '_config/au/additionalDebtsDetails';
import { isDCLOn } from 'featureToggles';
import {
  useFormInitialValues,
  useSaveApplication,
  useErrorTrackingAndUpdateStore,
  useDeliverApplication,
} from '../../hooks';
import { useSteps } from '../../hooks/useSteps';
import { NegativeCapacityModalContent } from './NegativeCapacityModalContent';

const FORM_ID = 'additionalDebtsDetails';
const formConfig = initFormConfig(config.formConfigs[FORM_ID]);
const editButtonText = 'Edit finances';

export function AdditionalDebtsDetails() {
  const history = useHistory();
  const [storeState, updateStore] = useStoreValue();
  const { acquisition, ...otherState } = storeState;
  const { trackEvent } = useEventTracking();
  const initialValues = useFormInitialValues(FORM_ID);
  const { handleErrorCallback } = useErrorTrackingAndUpdateStore('Next');
  const [isWarningModalOpen, setWarningModalOpen] = useState(false);
  const onModalClose = useCallback(
    additionalDebtsDetails => {
      trackEvent({
        event: {
          category: 'application',
          action: 'application-navigation',
          location: 'commitments',
          label: editButtonText,
        },
        ...getDataLayerElements({
          ...storeState,
          acquisition: {
            ...storeState.acquisition,
            additionalDebtsDetails,
          },
        }),
      });
      setWarningModalOpen(false);
    },
    [setWarningModalOpen, trackEvent, storeState],
  );
  const { check, warnings } = useWarning();
  const { nextStep, pathname, isLastDraftStep } = useSteps();
  const submitButtonLabel = byCountry({
    AU: !shouldShowAch(storeState?.applyAndBuy?.channel) ? 'Next - Choose credit limit' : 'Next',
    NZ: 'Next',
  });

  const modalConfirmButtonLabel = 'Confirm and continue';
  const [customWarnings, setCustomWarnings] = useState(warnings);

  const goToNextPage = useCallback(
    (newAcquisition, newValues) => {
      const isAuAndNegativeCapacity =
        isAU() && newValues ? getNegativeCapacity(storeState, newValues).isNegativeCapacity : false;
      trackEvent({
        event: {
          category: 'application',
          action: isLastDraftStep ? 'application-submitted' : 'application-navigation',
          location: 'commitments',
          label: isAuAndNegativeCapacity ? modalConfirmButtonLabel : submitButtonLabel,
        },
        ...getDataLayerElements({
          ...storeState,
          ...(newAcquisition ? { acquisition: newAcquisition } : {}),
        }),
      });
      updateStore({
        activeStep: nextStep,
      });
      history.push(nextStep);
    },
    [storeState, trackEvent, isLastDraftStep, submitButtonLabel, updateStore, nextStep, history],
  );

  const clearErrorAndGoNext = () => {
    updateStore({
      applicationErrors: null,
      applicationSubmitted: true,
    });
    goToNextPage();
  };

  const { deliver, loading: loadingForSaveAndSubmit } = useDeliverApplication({
    storeState,
    onError: handleErrorCallback,
    onSuccess: clearErrorAndGoNext,
  });

  const { save, loading } = useSaveApplication({
    storeState,
    onError: handleErrorCallback,
  });

  const onSubmit = useCallback(
    ({ values, errors }) => {
      const newAcquisition = {
        ...acquisition,
        additionalDebtsDetails: { ...values },
      };

      const updateStoreFormValues = () => {
        updateStore({
          acquisition: newAcquisition,
        });
      };

      const onNzSubmit = () => {
        updateStoreFormValues();

        if (isLastDraftStep) {
          save(newAcquisition);
        }

        updateStore({
          applicationErrors: null,
        });
        goToNextPage(newAcquisition, values);
      };

      const onAuSubmit = () => {
        updateStoreFormValues();
        if (!isLastDraftStep) {
          goToNextPage(newAcquisition, values);
        } else {
          deliver(newAcquisition);
        }
      };

      submitHandler({
        submit: byCountry({ AU: onAuSubmit, NZ: onNzSubmit }),
        errors,
      });
    },
    [acquisition, goToNextPage, isLastDraftStep, save, deliver, storeState, updateStore],
  );

  const retrySubmit = () => {
    trackEvent({
      event: {
        category: 'application',
        action: 'application-submitted',
        location: pathname,
        label: 'Retry',
      },
    });

    onSubmit({ values: storeState.acquisition.additionalDebtsDetails });
  };

  const onClickNext = useCallback(
    ({ values, errors }) => {
      if (Object.keys(errors).length > 0) {
        return;
      }
      if (isAU()) {
        const { isNegativeCapacity, ...negativeCapacityValues } = getNegativeCapacity(storeState, values);
        if (!check(values)) {
          setWarningModalOpen(true);
        } else if (isNegativeCapacity) {
          const newWarnings = [];
          newWarnings.customContent = <NegativeCapacityModalContent {...negativeCapacityValues} />;
          setCustomWarnings(newWarnings);
          setWarningModalOpen(true);
        } else {
          onSubmit({ values, errors });
        }
      } else {
        onSubmit({ values, errors });
      }
    },
    [check, onSubmit, storeState],
  );

  if (isDCLOn() && otherState.applicationErrors) {
    const retry = byCountry({
      AU: retrySubmit,
      NZ: [APOLLO_NETWORK_ERROR, APOLLO_UNKNOWN_ERROR].includes(otherState.applicationErrors.type) ? retrySubmit : null,
    });
    return (
      <LayoutPage>
        <Errors
          applicationRef={otherState.applicationRef}
          retry={retry}
          retrying={byCountry({
            NZ: loading,
            AU: loadingForSaveAndSubmit,
          })}
          isPreSubmission
        />
      </LayoutPage>
    );
  }

  return (
    <LayoutPage noSessionTimeout>
      <Form
        id={FORM_ID}
        initialValues={initialValues}
        onSubmit={onClickNext}
        showSaveDraftButton
        saveDraftOnSubmit={!isLastDraftStep}
        saveDraftOnSessionTimeout
        submitButtonLabel={submitButtonLabel}
      >
        {formData => {
          return (
            <LayoutContent>
              <FormRenderer config={formConfig} formData={formData} />
              <WarningModal
                title="Can you confirm these finances are correct?"
                isOpen={isWarningModalOpen}
                onClose={() => onModalClose(formData.state.values)}
                onSubmit={() => onSubmit(formData.state)}
                warnings={warnings?.length > 0 ? warnings : customWarnings}
                editButtonText={editButtonText}
                confirmButtonText={modalConfirmButtonLabel}
              />
            </LayoutContent>
          );
        }}
      </Form>
    </LayoutPage>
  );
}
